
import { Component, Prop, Mixins, Provide } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IProvider } from "@/models/providers";
import {
  IMetaDataProvider,
  IMetaDataProviderMethods
} from "@/models/providers/metaData";
import { IState } from "@/store";
import { IMetaData } from "@/models/metaData";

@Component({
  computed: {
    ...mapState({
      metaDataScope(state: IState, getters) {
        return getters[`data/${DataModules.META_DATA}/scope`](
          this.metaDataType,
          this.metaDataId
        );
      },
      metaData(state: IState, getters): IMetaData {
        return (
          getters["data/single"]({
            scope: this.metaDataScope,
            storeModule: DataModules.META_DATA
          }) || {}
        );
      }
    })
  }
})
export default class MetaDataProvider extends Mixins(RequestPromises)
  implements IProvider {
  // Map state

  public metaDataScope!: string;
  public metaData!: IMetaData;

  // Props

  @Prop({ default: true })
  public metaDataType!: boolean;

  @Prop({ required: true })
  public metaDataId!: string;

  @Prop({ default: true })
  public handleGuarding!: boolean;

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  // Data

  public isLoading = true;
  public hasRequestError = false;
  public isReloading = false;
  public isProcessing = false;

  // Life-cycle methods

  public async created() {
    if (!this.contentIsAvailable) return;

    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch(`data/binSingle`, {
      storeModule: DataModules.META_DATA,
      scope: this.metaDataScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await this.getMetaData();

      this.hasRequestError = false;
    } catch (error) {
      if (this.$store.getters["api/is404"](error)) return;
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;

    return this.loadData();
  }

  public getMetaData() {
    return this.$store.dispatch(`data/${DataModules.META_DATA}/get`, {
      id: this.metaDataId,
      type: this.metaDataType,
      scope: this.metaDataScope,
      ignoreStored: this.isReloading,
      vm: this
    });
  }

  // Computed

  public get mappedMetaData() {
    const meta = this.metaData.meta;
    return _.isArray(meta) ? {} : meta;
  }

  // Guarding

  public guardFunctionalities = ["show_object_meta_data"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get canUpdate() {
    return this.$userCan("create_update_object_meta_data");
  }

  // Provide

  @Provide("metaDataProvider")
  public providerData(): IMetaDataProvider {
    return {
      canUpdate: this.canUpdate,
      contentIsAvailable: this.contentIsAvailable,
      isLoading: this.isLoading,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError,
      guardFunctionalities: this.guardFunctionalities,
      metaData: this.mappedMetaData
    };
  }

  @Provide("metaDataProviderMethods")
  public get providerMethods(): IMetaDataProviderMethods {
    return {
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $metaData: this.providerData(),
        $metaDataMethods: this.providerMethods
      })
    );
  }
}
